<script setup>
import { onBeforeUnmount,ref,watch } from 'vue'
import { cloudApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'
import { publicationMetricText } from '@/utils/publicationMetrics.js'
const props=defineProps({tenantId:String,projectId:String,filters:Object})
const result=ref(null),busy=ref(false),error=ref(''),offset=ref(0)
let sequence=0,disposed=false
const scope=()=>JSON.stringify([props.tenantId,props.projectId,props.filters,authStore.state.user?.id,offset.value])
async function load() {
  const owner=scope(),ticket=++sequence
  busy.value=true;error.value=''
  try {
    const query=new URLSearchParams({offset:String(offset.value),limit:'25'})
    for(const key of ['start_date','end_date','user_name','platform','account_identifier'])if(props.filters?.[key])query.set(key,props.filters[key])
    const payload=await cloudApi.get(`/api/tenants/${encodeURIComponent(props.tenantId)}/projects/${encodeURIComponent(props.projectId)}/publication-samples?${query}`)
    if(payload.schema_version!=='publication_account_samples.v1' || !Array.isArray(payload.items))throw new Error('效果记录暂时无法读取。')
    if(!disposed && sequence===ticket && owner===scope())result.value=payload
  }catch(e){if(!disposed && owner===scope())error.value=e.message}
  finally{if(sequence===ticket)busy.value=false}
}
function page(step){offset.value=Math.max(0,offset.value+step);load()}
watch(()=>JSON.stringify([props.tenantId,props.projectId,props.filters,authStore.state.user?.id]),()=>{result.value=null;offset.value=0;load()},{immediate:true})
onBeforeUnmount(()=>{disposed=true;sequence++})
</script>
<template>
  <details class="samples"><summary>发布后的效果采样记录</summary>
    <p>沿用上方任务日期、用户和账号筛选。每次采样单独保存；未采集的日期没有数据。播放、浏览和曝光分别展示。</p>
    <button type="button" :disabled="busy" @click="load">{{ busy ? '正在读取…' : '刷新已存采样' }}</button>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-if="result && !result.items.length">当前筛选尚无效果采样记录。免费自行确认不自动采集。</p>
    <article v-for="sample in result?.items || []" :key="sample.sample_id">
      <h4>{{ sample.user_name || '用户名称待同步' }} · {{ sample.platform }} · {{ sample.account_identifier }}</h4>
      <p>任务日期 {{ sample.task_date }}；实际采样 {{ new Date(sample.sampled_at).toLocaleString() }}</p>
      <dl><template v-for="(metric,name) in sample.metrics" :key="name"><dt>{{ metric.definition }}</dt><dd>{{ publicationMetricText(metric) }}</dd></template></dl>
      <details v-if="sample.comments_included"><summary>本次取得 {{ sample.comments.length }} 条顶层评论样本</summary>
        <p>{{ sample.comments_sampled_at ? new Date(sample.comments_sampled_at).toLocaleString() : '评论采样时间待核对' }} · {{ sample.comments_complete ? '本页查询结束' : '部分公开样本' }}；不代表全部评论或回复。</p>
        <ul><li v-for="comment in sample.comments" :key="comment.id">{{ comment.text }}</li></ul>
      </details>
      <p v-else>本套餐未采集评论样本。</p>
    </article>
    <nav v-if="result"><button type="button" :disabled="busy || !offset" @click="page(-25)">上一页</button><span>{{ result.total }} 条实际采样</span><button type="button" :disabled="busy || offset+25>=result.total" @click="page(25)">下一页</button></nav>
  </details>
</template>
<style scoped>
.samples{padding:16px;background:#f6f9fa;border-radius:12px;color:#203f45}summary{font-weight:700;cursor:pointer}p{line-height:1.7}article{margin-top:16px;border-top:1px solid #dce8e7;overflow-wrap:anywhere}dl{display:grid;grid-template-columns:minmax(100px,1fr) 1fr;gap:8px}dd{margin:0}button{min-height:40px;padding:8px 12px;border:1px solid #c7dada;border-radius:8px;background:#fff;color:inherit}button:disabled{opacity:.5}nav{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between}li{margin-bottom:10px}
</style>
